import { useQuery } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import PeriodPicker from './PeriodPicker'
import type { TaskFilters, WorkStatus } from '../types'
import { WORK_STATUS_LABEL } from '../types'

interface Props {
  value: TaskFilters
  onChange: (next: TaskFilters) => void
  showStatus?: boolean
}

/** Панель фильтров: месяц / клиент / источник / специалист / статус / поиск. */
export default function Filters({ value, onChange, showStatus = true }: Props) {
  const { canSeeAll } = useAuth()

  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients() })
  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources() })
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users(),
    enabled: canSeeAll,
  })

  function patch(next: Partial<TaskFilters>) {
    onChange({ ...value, ...next })
  }

  return (
    <div className="toolbar">
      <div className="field">
        <label>Период</label>
        <PeriodPicker
          from={value.date_from}
          to={value.date_to}
          onChange={(date_from, date_to) => patch({ date_from, date_to })}
        />
      </div>

      <div className="field">
        <label>Клиент</label>
        <select
          value={value.client_id ?? ''}
          onChange={(e) => patch({ client_id: e.target.value ? Number(e.target.value) : undefined })}
        >
          <option value="">Все</option>
          {clients.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>Источник</label>
        <select
          value={value.source_id ?? ''}
          onChange={(e) =>
            patch({ source_id: e.target.value ? Number(e.target.value) : undefined })
          }
        >
          <option value="">Все</option>
          {sources.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {canSeeAll && (
        <div className="field">
          <label>Специалист</label>
          <select
            value={value.assignee_id ?? ''}
            onChange={(e) =>
              patch({ assignee_id: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value="">Все</option>
            {users.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {showStatus && (
        <div className="field">
          <label>Статус</label>
          <select
            value={value.work_status?.[0] ?? ''}
            onChange={(e) =>
              patch({ work_status: e.target.value ? [e.target.value as WorkStatus] : undefined })
            }
          >
            <option value="">Все</option>
            {Object.entries(WORK_STATUS_LABEL).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field" style={{ flex: 1, minWidth: 180 }}>
        <label>Поиск по теме</label>
        <input
          value={value.q ?? ''}
          placeholder="настройка сканера…"
          onChange={(e) => patch({ q: e.target.value || undefined })}
        />
      </div>
    </div>
  )
}
